/**
 * EVENTSFLO-DOCUMENT-AUDIT-RETENTION-STATUS-WIDGET-V1
 *
 * Read-only SuperAdmin widget that surfaces document_access_logs
 * retention state. Hits `GET /api/admin/document-access-logs/
 * retention/status` once on mount.
 *
 * Three rendered states:
 *   - never_run — green helper text "Retention has not been applied yet."
 *   - ok        — green badge, last-run timestamp, deleted counts
 *   - stale     — amber badge with "stale" message
 *
 * NO purge button. NO apply action. Pure visibility.
 */
import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { ShieldCheck, AlertTriangle, Info } from "lucide-react";
import { useAuth } from "../../../context/AuthContext";

const STATUS_LABEL = {
  never_run: "Never run",
  ok: "OK",
  stale: "Stale",
};

function formatDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export default function AuditRetentionStatusCard() {
  const { user, token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isSuperAdmin = user?.role === "superadmin";

  useEffect(() => {
    if (!isSuperAdmin || !token) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/admin/document-access-logs/retention/status`,
      { headers: { Authorization: `Bearer ${token}` } },
    )
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((body) => {
        if (!cancelled) setData(body);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message || "Failed to load");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isSuperAdmin, token]);

  // Hide for non-SuperAdmin.
  if (!isSuperAdmin) return null;

  const status = data?.status;
  const badgeTone =
    status === "ok"
      ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
      : status === "stale"
      ? "bg-amber-500/15 text-amber-300 border border-amber-500/30"
      : "bg-zinc-500/15 text-zinc-300 border border-zinc-500/30";

  return (
    <Card data-testid="audit-retention-status-card">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <ShieldCheck className="w-4 h-4" /> Audit retention status
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-sm text-zinc-400" data-testid="audit-retention-loading">
            Loading retention status…
          </div>
        ) : error ? (
          <div
            className="text-sm text-amber-400 flex items-start gap-2"
            data-testid="audit-retention-error"
          >
            <AlertTriangle className="w-4 h-4 mt-0.5" />
            <span>Could not load retention status ({error}).</span>
          </div>
        ) : data ? (
          <div
            className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm"
            data-testid="audit-retention-grid"
          >
            <div>
              <div className="text-zinc-400">Status</div>
              <div
                className="font-medium mt-0.5"
                data-testid="audit-retention-status-value"
              >
                <Badge className={badgeTone}>
                  {STATUS_LABEL[status] || status}
                </Badge>
              </div>
            </div>
            <div>
              <div className="text-zinc-400">Last purge</div>
              <div
                className="font-medium"
                data-testid="audit-retention-last-purge"
              >
                {status === "never_run"
                  ? "Never"
                  : formatDateTime(data.last_purge_ran_at)}
              </div>
            </div>
            <div>
              <div className="text-zinc-400">Retention (days)</div>
              <div className="font-medium">
                {data.last_retention_days_used ?? data.retention_days_default}
              </div>
            </div>
            <div>
              <div className="text-zinc-400">Last deleted</div>
              <div
                className="font-medium"
                data-testid="audit-retention-last-deleted"
              >
                {status === "never_run"
                  ? "—"
                  : `${data.last_total_deleted ?? 0} rows`}
              </div>
            </div>

            {status !== "never_run" && data.last_deleted_counts ? (
              <div className="col-span-2 md:col-span-4 text-xs text-zinc-500">
                document_access_logs:{" "}
                {data.last_deleted_counts.document_access_logs ?? 0}
                {" · "}
                permission_audit_logs:{" "}
                {data.last_deleted_counts.permission_audit_logs ?? 0}
              </div>
            ) : null}

            {/* EVENTSFLO-DOCUMENT-AUDIT-AUTO-PURGE-V1 — surface the
                daily scheduler state. Pure visibility, no controls. */}
            <div
              className="col-span-2 md:col-span-4 flex flex-wrap items-center gap-2 mt-1"
              data-testid="audit-retention-auto-purge-row"
            >
              <span className="text-zinc-400 text-xs">Automatic purge:</span>
              <Badge
                className={
                  data.automatic_purge_enabled
                    ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
                    : "bg-zinc-500/15 text-zinc-300 border border-zinc-500/30"
                }
                data-testid="audit-retention-auto-purge-badge"
              >
                {data.automatic_purge_enabled ? "Enabled" : "Disabled"}
              </Badge>
              {data.automatic_purge_enabled && data.next_scheduled_run ? (
                <span
                  className="text-xs text-zinc-500"
                  data-testid="audit-retention-next-run"
                >
                  next run: {formatDateTime(data.next_scheduled_run)} UTC
                </span>
              ) : null}
            </div>

            <div
              className="col-span-2 md:col-span-4 text-xs text-zinc-500 flex items-start gap-2 mt-1"
              data-testid="audit-retention-help"
            >
              <Info className="w-3.5 h-3.5 mt-0.5" />
              <span>
                {status === "never_run"
                  ? data.message
                  : data.automatic_purge_enabled
                  ? "Retention runs automatically on the scheduled cadence. Manual purge remains available."
                  : "Retention is manual. No automatic purge is enabled."}
              </span>
            </div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
